import {useCallback, useEffect, useRef, useState} from "react";
import {Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Typography} from "@mui/material";
import {CloudUpload as CloudUploadIcon, InsertDriveFile as FileIcon, CheckCircle as CheckCircleIcon, Error as ErrorIcon, Download as DownloadIcon, Close as CloseIcon} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import axiosInstance from "../../config/axios-config";

export default function ProductImport({uploadRequest = 0, onWorkflowOpened, onImported, onError, onBusyChange, disabled}) {
    const {t, i18n} = useTranslation();
    // File upload states
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
    const [selectedFile, setSelectedFile] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(null)
    const [processing, setProcessing] = useState(false) // Server-side processing state
    const [uploadResult, setUploadResult] = useState(null)
    const [dragActive, setDragActive] = useState(false)
    const fileInputRef = useRef(null)
    const request = useRef(null);
    useEffect(() => {
        const current = {active: true, pending: false, generation: 0, controller: new AbortController()};
        request.current = current;
        return () => {current.active = false; current.controller.abort();};
    }, []);

    // File upload handlers
    const handleOpenUploadDialog = useCallback(() => {
        if (request.current?.pending) return;
        setUploadDialogOpen(true)
        setSelectedFile(null)
        setUploadResult(null)
        setUploadProgress(null)
        setProcessing(false)
    }, [])

    useEffect(() => {
        if (uploadRequest && !disabled) {
            handleOpenUploadDialog();
            onWorkflowOpened?.();
        }
    }, [disabled, uploadRequest, onWorkflowOpened, handleOpenUploadDialog]);

    const handleCloseUploadDialog = () => {
        if (request.current?.pending) return;
        setUploadDialogOpen(false)
        setSelectedFile(null)
        setUploadResult(null)
        setUploadProgress(null)
        setProcessing(false)
    }

    const handleDrag = (e) => {
        e.preventDefault()
        e.stopPropagation()
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true)
        } else if (e.type === "dragleave") {
            setDragActive(false)
        }
    }

    const handleDrop = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setDragActive(false)

        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileSelect(e.dataTransfer.files[0])
        }
    }

    const handleFileInputChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0])
        }
    }

    const handleFileSelect = (file) => {
        if (request.current?.pending) return;
        const validTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
        ]
        const validExtensions = ['.xls', '.xlsx', '.csv']
        const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase()

        if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
            setSelectedFile(null);
            setUploadResult({
                success: false,
                message: t("InvalidFileType") || "Invalid file type. Please upload Excel (.xls, .xlsx) or CSV (.csv) file."
            })
            return
        }

        setSelectedFile(file)
        setUploadResult(null)
    }

    const handleUploadFile = async () => {
        const current = request.current;
        if (!selectedFile || !current?.active || current.pending) return;
        current.pending = true;
        const generation = ++current.generation;
        onBusyChange?.(true);

        setUploading(true)
        setUploadProgress(null)
        setProcessing(false)

        const formData = new FormData()
        formData.append('file', selectedFile)

        try {
            const response = await axiosInstance.post('/api/v1/product_tenants/upload', formData, {
                signal: current.controller.signal,
                timeout: 120000,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
                onUploadProgress: (progressEvent) => {
                    if (!current.active || !current.pending || current.generation !== generation) return;
                    if (!progressEvent.total) return;
                    const progress = Math.min(100, Math.max(0, Math.round((progressEvent.loaded * 100) / progressEvent.total)))
                    setUploadProgress(progress)
                    // When upload reaches 100%, switch to processing state
                    if (progress === 100) {
                        setUploading(false)
                        setProcessing(true)
                    }
                }
            })

            if (!current.active) return;
            if (!["totalCount", "insertedCount", "existingCount", "notFoundCodes"].every(key =>
                Number.isSafeInteger(response.data?.[key]) && response.data[key] >= 0)) throw new Error("Unconfirmed import result");

            setProcessing(false)
            setUploadResult({
                success: true,
                message: response.data.message || t("UploadSuccess"),
                details: response.data
            })

            // Refresh products list
            onImported()

        } catch (error) {
            if (!current.active) return;
            setProcessing(false)
            setUploadResult({
                success: false,
                message: error.response?.data?.message || t("ProductsImportUnconfirmed"),
                errors: error.response?.data?.errors || []
            })
        } finally {
            current.pending = false;
            if (current.active) {
                onBusyChange?.(false);
                setUploading(false);
                setProcessing(false);
            }
        }
    }

    const handleDownloadTemplate = async () => {
        const current = request.current;
        if (!current?.active || current.pending) return;
        try {
            const response = await axiosInstance.get('/api/v1/product_tenants/template', {
                signal: current.controller.signal,
                responseType: 'blob',
                headers: {
                    "Language-Context": i18n.language.toUpperCase() || "EN",
                },
            })

            if (!current.active) return;

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'products_template.xlsx')
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            if (!current.active) return;
            console.error("Failed to download template:", error)
            onError(t("TemplateDownloadFailed") || "Failed to download template.")
        }
    }

    return <>
        <Button variant="contained" startIcon={<CloudUploadIcon/>} onClick={handleOpenUploadDialog} disabled={disabled}>{t("UploadProducts")}</Button>
        <Dialog
            open={uploadDialogOpen}
            onClose={handleCloseUploadDialog}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <CloudUploadIcon color="primary"/>
                    {t("UploadProducts") || "Upload Products"}
                </Box>
                <IconButton onClick={handleCloseUploadDialog} size="small" disabled={uploading || processing} aria-label={t("Close")}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {/* Download Template Button */}
                <Box sx={{mb: 3}}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon/>}
                        onClick={handleDownloadTemplate}
                        disabled={uploading || processing}
                        size="small"
                    >
                        {t("DownloadTemplate") || "Download Template"}
                    </Button>
                    <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 0.5}}>
                        {t("DownloadTemplateHint") || "Download and fill the template with your product data"}
                    </Typography>
                </Box>

                {/* Drag and Drop Zone */}
                <Box
                    role="button" tabIndex={uploading || processing ? -1 : 0}
                    aria-label={t("OrClickToSelect")} aria-disabled={uploading || processing}
                    onKeyDown={event => {if (!uploading && !processing && ["Enter", " "].includes(event.key)) {
                        event.preventDefault(); fileInputRef.current?.click();
                    }}}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => {if (!uploading && !processing) fileInputRef.current?.click();}}
                    sx={{
                        border: "2px dashed",
                        borderColor: dragActive ? "primary.main" : "grey.300",
                        borderRadius: 2,
                        p: 4,
                        textAlign: "center",
                        cursor: "pointer",
                        backgroundColor: dragActive ? "action.hover" : "background.paper",
                        transition: "all 0.2s ease",
                        "&:hover": {
                            borderColor: "primary.main",
                            backgroundColor: "action.hover",
                        }
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xls,.xlsx,.csv"
                        onChange={handleFileInputChange}
                        disabled={uploading || processing}
                        aria-label={t("UploadProducts")}
                        style={{display: "none"}}
                    />
                    <CloudUploadIcon sx={{fontSize: 48, color: "primary.main", mb: 1}} />
                    <Typography variant="body1" gutterBottom>
                        {t("DragDropFile") || "Drag and drop your file here"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t("OrClickToSelect") || "or click to select a file"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{display: "block", mt: 1}}>
                        {t("SupportedFormats") || "Supported formats: .xls, .xlsx, .csv"}
                    </Typography>
                </Box>

                {/* Selected File Info */}
                {selectedFile && (
                    <Box sx={{mt: 2, p: 2, backgroundColor: "grey.50", borderRadius: 1, display: "flex", alignItems: "center", gap: 2}}>
                        <FileIcon color="primary" />
                    <Box sx={{flex: 1, minWidth: 0}}>
                        <Typography variant="body2" fontWeight="medium" sx={{overflowWrap: "anywhere"}}>
                                {selectedFile.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {(selectedFile.size / 1024).toFixed(2)} KB
                            </Typography>
                        </Box>
                        <IconButton size="small" disabled={uploading || processing} aria-label={t("Remove")}
                            onClick={() => setSelectedFile(null)}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                )}

                {/* Upload Progress */}
                {uploading && (
                    <Box sx={{mt: 2}}>
                    <LinearProgress variant={uploadProgress === null ? "indeterminate" : "determinate"}
                        value={uploadProgress ?? 0} aria-label={t("Uploading")}/>
                        <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: "block"}}>
                        {t("Uploading") || "Uploading..."} {uploadProgress === null ? "" : `${uploadProgress}%`}
                        </Typography>
                    </Box>
                )}

                {/* Processing State */}
                {processing && (
                    <Box sx={{mt: 2}}>
                        <LinearProgress variant="indeterminate" />
                        <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: "block"}}>
                            {t("ProcessingProducts") || "Processing products on server... Please wait."}
                        </Typography>
                    </Box>
                )}

                {/* Upload Result */}
                {uploadResult && (
                    <Alert
                        severity={uploadResult.success ? uploadResult.details?.notFoundCodes > 0 ? "warning" : "success" : "error"}
                        role={uploadResult.success ? "status" : "alert"}
                        sx={{mt: 2}}
                        icon={uploadResult.success ? <CheckCircleIcon /> : <ErrorIcon />}
                    >
                        <Typography variant="body2">{uploadResult.message}</Typography>
                        {uploadResult.details && (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                {uploadResult.details.insertedCount === 0 && uploadResult.details.notFoundCodes === 0 ? (
                                    t("AllProductsAlreadyProcessed") || "No products were processed."
                                ) : (
                                    <>
                                        {t("ProductsImportSummary", {total: uploadResult.details.totalCount,
                                            inserted: uploadResult.details.insertedCount, existing: uploadResult.details.existingCount,
                                            missing: uploadResult.details.notFoundCodes})}
                                    </>
                                )}
                            </Typography>
                        )}
                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                            <List dense sx={{mt: 1}}>
                                {uploadResult.errors.slice(0, 5).map((error, index) => (
                                    <ListItem key={index} sx={{py: 0}}>
                                        <ListItemIcon sx={{minWidth: 24}}>
                                            <ErrorIcon fontSize="small" color="error" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={error}
                                            primaryTypographyProps={{variant: "caption"}}
                                        />
                                    </ListItem>
                                ))}
                                {uploadResult.errors.length > 5 && (
                                    <Typography variant="caption" color="text.secondary">
                                        ... {t("AndMore") || "and"} {uploadResult.errors.length - 5} {t("MoreErrors") || "more errors"}
                                    </Typography>
                                )}
                            </List>
                        )}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={handleCloseUploadDialog} color="inherit" disabled={uploading || processing}>
                    {t("Cancel") || "Cancel"}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleUploadFile}
                    disabled={!selectedFile || uploading || processing}
                    startIcon={(uploading || processing) ? null : <CloudUploadIcon />}
                >
                    {uploading ? `${t("Uploading") || "Uploading"}...` : processing ? `${t("Processing") || "Processing"}...` : t("Upload") || "Upload"}
                </Button>
            </DialogActions>
        </Dialog>
    </>;
}
